import axios from 'axios';
import {BASE_URL} from './config';
import LocalStorage from '../storage/LocalStorage';
import {storesProvider} from '../../App';

axios.defaults.withCredentials = true;

// Dropped requests hang forever without this
const REQUEST_TIMEOUT_MS = 120000;

const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: REQUEST_TIMEOUT_MS,
});

const anonClient = axios.create({
  baseURL: BASE_URL,
  timeout: REQUEST_TIMEOUT_MS,
});

function isUnauthorizedError(error: any) {
  if (!error || !error?.response || !error?.response?.status) {
    return false;
  }
  const {
    response: {status},
  } = error;
  return status === 401;
}

/*
 * Sessions last 15 minutes and each refresh token works once (the API revokes it as it issues
 * the next). So the session is renewed once for every request that finds it expired: requests
 * that fail together (Studio's polling, a page's parallel reads) share one refresh, a request
 * sent before a refresh that has since finished just tries again, and tabs take turns (Web
 * Locks) so two tabs never spend the same refresh token. A refresh that fails because the
 * server is busy or unreachable is tried again, and signs nobody out; only a refresh the
 * server refuses does.
 */

/** How a refresh ended: renewed, refused by the server, or the server could not be reached. */
export type RefreshOutcome = 'refreshed' | 'rejected' | 'unavailable';

/** How long to wait before trying a refresh again that failed on a busy server. */
const REFRESH_RETRY_MS = [500, 1500, 4000];
/**
 * How long one refresh may take, and how long a refresh may go on trying in all. A refresh holds
 * every expired request in this tab and the lock every other tab waits on, so a server that
 * does not answer fails it fast, and the next request (Studio's next poll) tries again.
 */
const REFRESH_TIMEOUT_MS = 10000;
const REFRESH_DEADLINE_MS = 20000;
/** The least time worth giving a refresh attempt before the deadline. */
const REFRESH_MIN_ATTEMPT_MS = 1000;
/** How long a refused refresh waits for another tab that may have just renewed the session. */
const REFRESH_RIVAL_MS = 1000;
/** When any tab last renewed the session (ms since the epoch). */
const REFRESHED_AT_KEY = 'session_refreshed_at';
const REFRESH_LOCK = 'tapybl-session-refresh';
/** Set on a request's config: when it was sent, and that it is a retry after a refresh. */
const SENT_AT = '__sentAt';
const RETRIED = '__retriedAfterRefresh';

let refreshing: Promise<RefreshOutcome> | null = null;
let refreshedHere = 0;
let loggingOut: Promise<void> | null = null;

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/** The status codes a busy or unreachable server answers with (0: no answer at all). */
const isTransient = (status: number | undefined) =>
  !status || status === 408 || status === 429 || status >= 500;

/** When this tab or another last renewed the session. */
function lastRefreshAt() {
  let shared = 0;
  try {
    shared = Number(localStorage.getItem(REFRESHED_AT_KEY)) || 0;
  } catch {
    // Storage can be unavailable; this tab's own refreshes still count.
  }
  return Math.max(refreshedHere, shared);
}

function markRefreshed() {
  refreshedHere = Date.now();
  try {
    localStorage.setItem(REFRESHED_AT_KEY, String(refreshedHere));
  } catch {
    // As above.
  }
}

/** Runs `renew` while no other tab refreshes, where the browser can tell. */
function withRefreshLock<T>(renew: () => Promise<T>): Promise<T> {
  const locks = (
    typeof navigator !== 'undefined'
      ? (navigator as {locks?: {request?: unknown}}).locks
      : undefined
  ) as
    | {request(name: string, callback: () => Promise<T>): Promise<T>}
    | undefined;
  return typeof locks?.request === 'function'
    ? locks.request(REFRESH_LOCK, renew)
    : renew();
}

async function renewToken(sentAt: number): Promise<RefreshOutcome> {
  const deadline = Date.now() + REFRESH_DEADLINE_MS;
  for (let attempt = 0; ; attempt++) {
    // Another tab renewed the session while this one waited its turn.
    if (lastRefreshAt() > sentAt) return 'refreshed';
    const response: any = await anonClient.post(
      '/authentication/refresh-token',
      {},
      {timeout: Math.min(REFRESH_TIMEOUT_MS, deadline - Date.now())},
    );
    if (response?.success && response.status === 200) {
      markRefreshed();
      storesProvider.getNavigationStore().tryGetMyOrganization();
      return 'refreshed';
    }
    if (!isTransient(response?.status)) {
      // A browser without Web Locks may have let another tab spend the same refresh token
      // a moment ago; its new session is this tab's too.
      await wait(REFRESH_RIVAL_MS);
      return lastRefreshAt() > sentAt ? 'refreshed' : 'rejected';
    }
    if (
      attempt >= REFRESH_RETRY_MS.length ||
      deadline - Date.now() - REFRESH_RETRY_MS[attempt] < REFRESH_MIN_ATTEMPT_MS
    )
      return 'unavailable';
    await wait(REFRESH_RETRY_MS[attempt]);
  }
}

/**
 * Renews the session for a request sent at `sentAt` that found it expired; by default,
 * renews it now. Concurrent callers share one refresh.
 */
export function refreshSession(sentAt = Date.now()): Promise<RefreshOutcome> {
  // A refresh finished after the request was sent: the request only needs sending again.
  if (lastRefreshAt() > sentAt) return Promise.resolve('refreshed');
  if (!refreshing) {
    refreshing = withRefreshLock(() => renewToken(sentAt))
      .catch((): RefreshOutcome => 'unavailable')
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

async function withAuth(headers: any) {
  if (!headers) {
    headers = {};
  }
  return headers;
}

apiClient.interceptors.request.use(config => {
  (config as any)[SENT_AT] = Date.now();
  return config;
});

apiClient.interceptors.response.use(
  res => ({
    success: true,
    ...res,
    data: res.data,
  }),
  async error => {
    const originalConfig = error?.config;
    const storage = new LocalStorage();

    const user = storage.getItemFromStorage(LocalStorage.Keys.USER_KEY);
    if (
      !isUnauthorizedError(error) ||
      !user ||
      !originalConfig ||
      originalConfig[RETRIED]
    ) {
      return resolveWithError(error);
    }

    const outcome = await refreshSession(originalConfig[SENT_AT] ?? Date.now());
    if (outcome === 'refreshed') {
      try {
        return await apiClient.request({...originalConfig, [RETRIED]: true});
      } catch (innerError) {
        return resolveWithError(error);
      }
    }
    // Only a refresh the server refused ends the session; a busy server just fails this request.
    if (outcome === 'rejected') await logout();
    return resolveWithError(error);
  },
);

/** Signs out once, however many requests found the session gone. */
const logout = () => {
  if (!loggingOut)
    loggingOut = (async () => {
      const storage = new LocalStorage();
      await anonClient.delete(`/authentication/invalidate_tokens`, {});
      storage.resetThemeColors();
      storage.wipeLocalStorage();
      storesProvider.reInitStores();
      window.location.reload();
    })();
  return loggingOut;
};

anonClient.interceptors.response.use(
  res => ({
    success: true,
    ...res,
    data: res.data,
  }),
  async error => {
    return resolveWithError(error);
  },
);

const resolveWithError = (error: any) => {
  if (error?.code === 'ECONNABORTED') {
    return Promise.resolve({
      success: false,
      status: error.response?.status || 0,
      errors: ['timeout'],
    });
  }
  if (!error.response?.data) {
    return Promise.resolve({
      success: false,
      status: error.response?.status || 0,
      errors: ['unknown'],
    });
  }
  if (
    error.response.data?.Errors &&
    Array.isArray(error.response.data?.Errors)
  ) {
    return Promise.resolve({
      success: false,
      status: error.response.status,
      errors: error.response.data.Errors,
    });
  } else if (error.response.data) {
    return Promise.resolve({
      success: false,
      status: error.response.status,
      errors: [error.response.data],
    });
  }
};

export {apiClient, withAuth, anonClient};
